/**
 * Barcode checks for codes a model returns. The search model (src/lib/aiModels.json)
 * often answers from memory without running a search, so a code is checked
 * before it is kept: the GS1 check digit rejects malformed ones, and Open Food
 * Facts, when it knows the code, confirms it belongs to the same brand. In a
 * 2026-11-02 test it confirmed all 7 codes the model returned for food items.
 */
const OFF_TIMEOUT_MS = 3000;
const brandChecks = new Map<string, Promise<BarcodeBrandCheck>>();

export type BarcodeBrandCheck = 'match' | 'mismatch' | 'unknown';

/** True for an 8, 12, 13 or 14 digit code whose last digit is its GS1 check digit. */
export const isValidGtin = (code: string): boolean => {
  if (!/^(\d{8}|\d{12,14})$/.test(code)) return false;
  const digits = code.split('').map(Number);
  const check = digits.pop()!;
  const sum = digits.reverse().reduce((total, d, i) => total + d * (i % 2 === 0 ? 3 : 1), 0);
  return (10 - (sum % 10)) % 10 === check;
};

const brandWords = (brand: string) =>
  brand.toLowerCase().normalize('NFKD').replace(/[^a-z0-9 ]/g, '').split(/\s+/).filter(w => w.length > 1);

const sameBrand = (a: string, b: string) => {
  const aw = brandWords(a);
  const bw = brandWords(b);
  return aw.some(w => bw.includes(w)) || aw.join('') === bw.join('');
};

/**
 * Whether Open Food Facts lists `code` under `brand`: 'unknown' when it doesn't
 * know the code (most non-food products) or can't be reached.
 */
export const checkBarcodeBrand = (code: string, brand: string): Promise<BarcodeBrandCheck> => {
  const key = `${code}:${brand.toLowerCase()}`;
  if (!brandChecks.has(key)) {
    brandChecks.set(key, (async () => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), OFF_TIMEOUT_MS);
      try {
        const res = await fetch(`https://world.openfoodfacts.org/api/v2/product/${code}?fields=brands`, {
          signal: controller.signal,
        });
        if (!res.ok) return 'unknown';
        const data = await res.json();
        const listed = data?.status === 1 ? String(data.product?.brands || '') : '';
        if (!listed) return 'unknown';
        return listed.split(',').some(b => sameBrand(b, brand)) ? 'match' : 'mismatch';
      } catch {
        return 'unknown';
      } finally {
        clearTimeout(timer);
      }
    })());
  }
  return brandChecks.get(key)!;
};

/** Drops a UPC/GTIN pair that Open Food Facts lists under another brand. */
export const dropOtherBrandCodes = async <T extends { upc?: string; gtin?: string }>(
  ids: T,
  brand?: string | null,
): Promise<T> => {
  const code = ids.upc || ids.gtin;
  if (!code || !brand?.trim()) return ids;
  if (await checkBarcodeBrand(code, brand) !== 'mismatch') return ids;
  console.warn(`[barcode] ${code} belongs to another brand than "${brand}"; discarding it.`);
  return { ...ids, upc: undefined, gtin: undefined };
};
